import { createContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";

// ─── Module-level trigger registry ───────────────────────────────────
// fireConfetti() can be called from anywhere (hooks, plain functions, services)
// without needing to live inside ConfettiProvider's React tree.
let registeredTrigger: (() => void) | null = null;

export function fireConfetti(): void {
  registeredTrigger?.();
}

// ─── Context (kept private — module emitter is the public API) ───────
const ConfettiContext = createContext<{ trigger: () => void } | null>(null);

// ─── Provider ────────────────────────────────────────────────────────
export function ConfettiProvider({ children }: { children: ReactNode }) {
  const [bursts, setBursts] = useState<number[]>([]);

  const trigger = useCallback(() => {
    const id = Date.now() + Math.random();
    setBursts((prev) => [...prev, id]);
    // Auto-cleanup after burst lifecycle completes
    window.setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b !== id));
    }, 3500);
  }, []);

  // Register module-level trigger so fireConfetti() works from non-React callers
  useEffect(() => {
    registeredTrigger = trigger;
    return () => { registeredTrigger = null; };
  }, [trigger]);

  return (
    <ConfettiContext.Provider value={{ trigger }}>
      {children}
      {bursts.map((id) => <ConfettiBurst key={id} />)}
    </ConfettiContext.Provider>
  );
}

// ─── Burst Canvas (one per fire, auto-unmounts) ──────────────────────
function ConfettiBurst() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Honor reduced-motion: skip animation entirely
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const dpr = window.devicePixelRatio || 1;
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    // Phase accent palette — confetti carries the same color language as the lanes
    const colors = ["#3B82F6", "#A855F7", "#F59E0B", "#14B8A6", "#10B981", "#EC4899", "#FBBF24"];

    interface Particle {
      x: number; y: number;
      vx: number; vy: number;
      rot: number; vrot: number;
      size: number;
      color: string;
      shape: "rect" | "circle";
      life: number;
    }

    const particles: Particle[] = [];
    const COUNT = 90;
    const originX = W / 2;
    const originY = H * 0.4;

    for (let i = 0; i < COUNT; i++) {
      const angle = (Math.PI * 2 * i) / COUNT + (Math.random() - 0.5) * 0.4;
      const speed = 4 + Math.random() * 7;
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2.5, // bias upward for that "burst" feel
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.3,
        size: 6 + Math.random() * 6,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: Math.random() > 0.4 ? "rect" : "circle",
        life: 1,
      });
    }

    const GRAVITY = 0.18;
    const DRAG = 0.992;
    const FADE = 0.008;

    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, W, H);

      for (const p of particles) {
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;
        p.life -= FADE;

        if (p.life <= 0) continue;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (elapsed < 3000) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 9000, // below toasts (~9999) so "Order saved" still reads on top
      }}
      aria-hidden="true"
    />
  );
}